import { createPnlSnapshotCache } from 'src/components/star-grid/services/pnlSnapshotCache';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PNL_REPORT_POLL_INTERVAL_MS } from 'src/components/star-grid/starGridConstants';
import { useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { loadPnlReport } from 'src/components/star-grid/services/loadPnlReport';
import { deletePnlReport, QueryTooLargeError } from 'src/components/star-grid/services/starGridService';
import { currentReportSelection, defaultReportSelection, PnlReportSelection } from 'src/components/star-grid/utils/pnlReportState';

export interface OversizedResult {
  readonly estimatedRows: number;
  readonly rowLimit: number;
  readonly hint: string;
}
type LoadedReport = Awaited<ReturnType<typeof loadPnlReport>>;
interface ReportState extends Partial<LoadedReport> {
  readonly reportId: number;
  readonly loading: boolean;
  readonly error?: string;
  readonly oversized?: OversizedResult;
}

const useReportRequests = (reportId: number) => {
  const text = useDataSyncText();
  const errorText = useRef(text('reportLoadFailed'));
  errorText.current = text('reportLoadFailed');
  const [state, setState] = useState<ReportState>({ reportId, loading: true });
  const active = useRef<AbortController>();
  const snapshotCache = useMemo(() => createPnlSnapshotCache(), []);
  useEffect(() => () => snapshotCache.clear(), [snapshotCache]);
  const latest = useRef<LoadedReport>();
  const currentId = useRef(reportId);
  const selectedVersion = useRef<string>();
  const run = useCallback(
    async (refresh: boolean, chosen?: PnlReportSelection, bypassSnapshotCache = false): Promise<void> => {
      active.current?.abort();
      const controller = new AbortController();
      active.current = controller;
      if (currentId.current !== reportId) {
        latest.current = undefined;
        currentId.current = reportId;
      }
      const cached = latest.current;
      const selection = chosen ?? (cached ? currentReportSelection(cached) : undefined);
      if (selection) selectedVersion.current = selection.asOfTimestamp;
      setState((previous) => ({
        ...(previous.reportId === reportId ? previous : {}),
        ...selection,
        reportId,
        loading: true,
        error: undefined,
        oversized: undefined
      }));
      try {
        const loaded = await loadPnlReport({
          reportId,
          snapshotCache,
          bypassSnapshotCache,
          asOfTimestamp: selectedVersion.current,
          definition: refresh ? undefined : cached?.definition,
          presentation: cached?.response,
          selection,
          signal: controller.signal,
          onDefinition: (definition, selected) => {
            latest.current = { definition, ...selected, model: cached?.model, response: cached?.response };
            setState((previous) => ({ ...previous, definition, ...selected }));
          }
        });
        if (controller.signal.aborted) return;
        latest.current = loaded;
        setState({ ...loaded, reportId, loading: false });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          loading: false,
          model: undefined,
          response: undefined,
          error: error instanceof QueryTooLargeError ? undefined : error instanceof Error ? error.message : errorText.current,
          oversized:
            error instanceof QueryTooLargeError ? { estimatedRows: error.estimatedRows, rowLimit: error.rowLimit, hint: error.hint } : undefined
        }));
      }
    },
    [reportId, snapshotCache]
  );
  useEffect(() => {
    void run(true);
    return () => active.current?.abort();
  }, [run]);
  const visible: ReportState = state.reportId === reportId ? state : { reportId, loading: true };
  return { state: visible, run, latest };
};

const useReportPolling = (state: ReportState, reload: () => void): void => {
  useEffect(() => {
    if (state.loading || state.error || state.definition?.status !== 'GENERATING') return undefined;
    const timeout = window.setTimeout(reload, PNL_REPORT_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [state.loading, state.error, state.definition, reload]);
};

const useReportSelection = (request: ReturnType<typeof useReportRequests>) => {
  const reload = useCallback(() => {
    void request.run(true, undefined, true);
  }, [request.run]);
  const setFilters = useCallback(
    (filters: Record<string, readonly string[]>): void => {
      const current = request.latest.current;
      if (current) void request.run(false, { ...currentReportSelection(current), filters });
    },
    [request.run, request.latest]
  );
  const drillDown = useCallback(
    (dimension: string): void => {
      const current = request.latest.current;
      if (current && !current.groupBy.includes(dimension))
        void request.run(false, { ...currentReportSelection(current), groupBy: [...current.groupBy, dimension] });
    },
    [request.run, request.latest]
  );
  const resetDrill = useCallback((): void => {
    const current = request.latest.current;
    if (current) void request.run(false, { ...defaultReportSelection(current.definition), asOfTimestamp: current.asOfTimestamp });
  }, [request.run, request.latest]);
  const setVersion = useCallback(
    (asOfTimestamp?: string): void => {
      const current = request.latest.current;
      if (current) void request.run(false, { ...currentReportSelection(current), asOfTimestamp });
    },
    [request.run, request.latest]
  );
  return { reload, setFilters, drillDown, resetDrill, setVersion };
};

export const useStarGridPnlReport = (reportId: number, onDeleted: () => void) => {
  const request = useReportRequests(reportId);
  const actions = useReportSelection(request);
  const [deleting, setDeleting] = useState(false);
  useReportPolling(request.state, actions.reload);
  const groupBy = request.state.groupBy ?? [];
  const definition = request.state.definition;
  const drillableDimensions = useMemo(
    () =>
      [...new Set([...(definition?.group_by_dimensions ?? []), ...(definition?.filter_by_dimension ?? [])])].filter(
        (field) => !groupBy.includes(field)
      ),
    [definition, groupBy]
  );
  const remove = async (): Promise<void> => {
    setDeleting(true);
    try {
      await deletePnlReport(reportId);
      onDeleted();
    } finally {
      setDeleting(false);
    }
  };
  return { ...request.state, groupBy, filters: request.state.filters ?? {}, drillableDimensions, deleting, remove, ...actions };
};
